import { laundryProfile, previewModifications } from '@/mocks/laundryProfile';
import { systemSettings } from '@/mocks/settings';
import type {
  LaundryCoverageOptions,
  LaundryModificationRequest,
  LaundryProfile,
  SettingsData,
} from '@/types';
import { delay, shouldSimulateError } from './simulation';

/**
 * Demo credential check. Only used for the local mock password-change flow;
 * no password is ever persisted or written to browser storage.
 */
const DEMO_CURRENT_PASSWORD = '12345678';

function coverageFor(cityId: string): LaundryCoverageOptions {
  const city = systemSettings.cities.find((item) => item.id === cityId) ?? systemSettings.cities[0];
  return { cityId: city.id, cityName: city.name, districts: [...city.districts] };
}

/** Preview helper: ?modState=none|under_review|approved|rejected switches the demo state. */
function readStateOverride(): 'none' | 'under_review' | 'approved' | 'rejected' {
  if (typeof window === 'undefined') return 'under_review';
  const value = new URLSearchParams(window.location.search).get('modState');
  if (value === 'none' || value === 'approved' || value === 'rejected') return value;
  return 'under_review';
}

/** The approved data currently in effect (updated by the service, never in the UI). */
let approvedProfile: LaundryProfile = { ...laundryProfile, address: { ...laundryProfile.address } };
let currentModification: LaundryModificationRequest | null = null;

function cloneProfile(profile: LaundryProfile): LaundryProfile {
  return {
    ...profile,
    address: { ...profile.address },
    coverageDistricts: [...profile.coverageDistricts],
  };
}

export async function fetchSettings(): Promise<SettingsData> {
  await delay();
  if (shouldSimulateError('settings')) {
    throw new Error('failed-to-load-settings');
  }

  const override = readStateOverride();

  if (override === 'none') {
    currentModification = null;
    approvedProfile = { ...laundryProfile, address: { ...laundryProfile.address } };
  } else {
    const template = previewModifications[override];
    currentModification = {
      ...template,
      previous: cloneProfile(template.previous),
      proposed: cloneProfile(template.proposed),
    };
    // An approved request means the system updated the approved data.
    approvedProfile = cloneProfile(override === 'approved' ? template.proposed : template.previous);
  }

  return {
    profile: cloneProfile(approvedProfile),
    modification: currentModification,
    coverage: coverageFor(approvedProfile.address.cityId),
  };
}

export function getCoverageOptions(cityId: string): LaundryCoverageOptions {
  return coverageFor(cityId);
}

/**
 * Sends a modification request. The proposed values are NOT applied to the
 * approved data — the request simply enters the "under review" state.
 */
export async function submitModification(proposed: LaundryProfile): Promise<LaundryModificationRequest> {
  await delay(800);
  if (shouldSimulateError('settingsSubmit')) {
    throw new Error('failed-to-submit-modification');
  }
  const request: LaundryModificationRequest = {
    id: `MOD-${Date.now().toString().slice(-5)}`,
    status: 'under_review',
    submittedAt: new Date().toISOString(),
    rejectionReason: null,
    previous: cloneProfile(approvedProfile),
    proposed: cloneProfile(proposed),
  };
  currentModification = request;
  return request;
}

export type ChangePasswordResult = { ok: true } | { ok: false; error: 'wrong_current' };

/**
 * Demo password change. Compares against the demo credential only; nothing is
 * stored, and the session is never modified here (follows the backend result).
 */
export async function changePassword(
  currentPassword: string,
  newPassword: string,
): Promise<ChangePasswordResult> {
  await delay(700);
  if (shouldSimulateError('password')) {
    throw new Error('failed-to-change-password');
  }
  if (currentPassword !== DEMO_CURRENT_PASSWORD) {
    return { ok: false, error: 'wrong_current' };
  }
  void newPassword;
  return { ok: true };
}